import type {LazyImageProps, StyleProps} from '@miro-site/core'
import {Box, Button, ButtonProps, LazyImage, Text} from '@miro-site/core'
import React, {useEffect, useRef} from 'react'

import {Transition} from 'react-transition-group'
import {useTheme} from 'styled-components'
import useFullscreenModal from '../../../hooks/useFullscreenModal'
import TransparentButton from '../../button/TransparentButton'
import Popup from '../../popup/Popup'
import {TemplateActions} from '../templateActions'
import {CardTags} from './CardTags'

export interface CardDescriptionProps {
  onClose?: () => void
  templateActions: TemplateActions
  isFullscreen?: boolean
  isShown?: boolean
  metadataId: string
  templatePagePath: string
  previewButton?: (defaultButton: React.ReactElement) => React.ReactElement
  actionButtonProps?: Partial<ButtonProps>
  thumbnail: LazyImageProps
  title: string
  description: string
  tags: string[] | null
  popupStyles?: StyleProps
  headingStyles?: StyleProps
  descriptionStyles?: StyleProps
}

const transitionStyles = {
  entering: {opacity: 1, visibility: 'visible'},
  entered: {opacity: 1, visibility: 'visible'},
  exiting: {opacity: 0, visibility: 'visible'},
  exited: {opacity: 0, visibility: 'hidden'},
}

const defaultStyle = {
  transition: `opacity 250ms ease-in-out, visibility 250ms ease-in-out`,
  opacity: 0,
  visibility: 'hidden',
  zIndex: 100,
}

const CardDescription: React.FC<CardDescriptionProps> = ({
  isShown = false,
  onClose,
  templateActions,
  isFullscreen,
  metadataId,
  templatePagePath,
  previewButton,
  actionButtonProps,
  thumbnail,
  title,
  description,
  popupStyles,
  headingStyles,
  descriptionStyles,
  tags,
}) => {
  if (isFullscreen) useFullscreenModal(isShown)

  const defaultPreviewButton = (
    <TransparentButton $size="medium" href={templatePagePath}>
      {templateActions.PREVIEW}
    </TransparentButton>
  )

  const popupRef = useRef<HTMLElement>(null)

  const handleOutsideClick = (e: MouseEvent) => {
    const clickOutsideNav = popupRef.current && !popupRef.current.contains(e.target as Node)

    if (clickOutsideNav && onClose) {
      onClose()
    }
  }

  useEffect(() => {
    if (!isShown) {
      return
    }

    document.addEventListener('click', handleOutsideClick, true)

    return () => {
      document.removeEventListener('click', handleOutsideClick, true)
    }
  }, [isShown])

  const theme = useTheme()

  return (
    <Transition in={isShown} timeout={250}>
      {state => (
        <Popup
          ref={popupRef}
          data-track-place="card-description"
          onCloseClick={onClose}
          $styles={{
            ...defaultStyle,
            ...popupStyles,
            ...transitionStyles[state],
          }}
          onMouseLeave={e => onClose && onClose()}
        >
          <Box>
            {isFullscreen && (
              <>
                {thumbnail && (
                  <Box
                    $mb={[20, null, null, null]}
                    $styles={{
                      position: 'relative',
                      overflow: 'hidden',
                      height: 0,
                      paddingTop: ['64.307%', null, null, null],
                    }}
                  >
                    <LazyImage {...thumbnail} detectIntersection={false} />
                  </Box>
                )}

                <Box
                  $mb={[40, null, null, null]}
                  $styles={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: '100%',
                  }}
                >
                  {actionButtonProps ? (
                    <Button
                      data-track-id="use-template"
                      data-template-id={metadataId}
                      $mr={[14, null, null, null]}
                      $buttonType="primary"
                      $size="medium"
                      onClick={actionButtonProps.onClick}
                      {...actionButtonProps}
                    >
                      {actionButtonProps.children || templateActions.USE_TEMPLATE}
                    </Button>
                  ) : null}
                  {previewButton ? previewButton(defaultPreviewButton) : defaultPreviewButton}
                </Box>
              </>
            )}
            <Text
              as="h4"
              $color="primaryIndigo"
              $mb={[24, 12, 12, 12]}
              $styles={{
                fontWeight: 700,
                fontSize: [24, 16, 16, 16],
                lineHeight: [32, 24, 24, 24],
                ...headingStyles,
              }}
            >
              {title}
            </Text>
            <CardTags
              descriptionStyles={descriptionStyles}
              headingStyles={headingStyles}
              tags={tags}
            />
            <Text
              $variant={['p1', 'p2', 'p2', 'p2']}
              $styles={{color: theme.templates.colors.secondary60, ...descriptionStyles}}
            >
              {description}
            </Text>
          </Box>
        </Popup>
      )}
    </Transition>
  )
}

export default CardDescription
